import { useState } from "react";
import { MapPin, Mail, Phone, Send, CheckCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch("https://formspree.io/f/myknbozk", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        setIsSubmitted(true);
        toast.success("Message sent successfully! We'll be in touch soon.");
        setFormData({ name: "", email: "", company: "", message: "" });
      } else {
        throw new Error("Form submission failed");
      }
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Header Section */}
      <section className="bg-white py-24 md:py-32" data-testid="contact-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-sm font-semibold tracking-wider uppercase text-orange-500 mb-4">
              Contact Us
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-tight mb-6">
              Get in Touch
            </h1>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed">
              Ready to discuss your storage needs? Fill out the form below and we'll get back to you promptly.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="bg-slate-50 py-24 md:py-32" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-8">
                Contact Information
              </h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-500 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Location</h3>
                    <p className="text-slate-600">
                      Units 1, 2 & 3 Plantation Road<br />
                      Burscough Industrial Estate<br />
                      Burscough, Lancashire L40 8JT
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-500 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Phone</h3>
                    <a 
                      href="tel:01704332544" 
                      className="text-slate-600 hover:text-orange-500 transition-colors"
                    >
                      01704 332544
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-500 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Email</h3>
                    <a 
                      href="mailto:info@palletspace.uk" 
                      className="text-slate-600 hover:text-orange-500 transition-colors"
                    >
                      info@palletspace.uk
                    </a>
                  </div>
                </div>
              </div>

              {/* Image */}
              <div className="mt-12 h-64 overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/10096900/pexels-photo-10096900.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Warehouse storage"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Form */}
            <div className="bg-white border border-slate-100 p-8 md:p-12">
              {isSubmitted ? (
                <div className="text-center py-12" data-testid="success-message">
                  <CheckCircle className="w-16 h-16 text-orange-500 mx-auto mb-6" />
                  <h3 className="font-heading text-2xl font-bold text-slate-900 mb-4">
                    Message Sent!
                  </h3>
                  <p className="text-slate-600 mb-8">
                    Thank you for contacting us. We'll get back to you as soon as possible.
                  </p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-6 transition-all duration-200"
                    data-testid="send-another"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form">
                  <h2 className="font-heading text-2xl font-bold text-slate-900 mb-8">
                    Send us a Message
                  </h2>
                  <div className="space-y-6">
                    <div>
                      <Label htmlFor="name" className="text-sm font-semibold text-slate-700 mb-2 block">
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="bg-slate-50 border-slate-200 focus:border-orange-500 focus:ring-orange-500/20 rounded-none h-12"
                        placeholder="Your name"
                        data-testid="input-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-sm font-semibold text-slate-700 mb-2 block">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="bg-slate-50 border-slate-200 focus:border-orange-500 focus:ring-orange-500/20 rounded-none h-12"
                        placeholder="your@email.com"
                        data-testid="input-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="company" className="text-sm font-semibold text-slate-700 mb-2 block">
                        Company Name
                      </Label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        value={formData.company}
                        onChange={handleChange}
                        className="bg-slate-50 border-slate-200 focus:border-orange-500 focus:ring-orange-500/20 rounded-none h-12"
                        placeholder="Your company (optional)"
                        data-testid="input-company"
                      />
                    </div>
                    <div>
                      <Label htmlFor="message" className="text-sm font-semibold text-slate-700 mb-2 block">
                        Message *
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        rows={5}
                        value={formData.message}
                        onChange={handleChange}
                        className="bg-slate-50 border-slate-200 focus:border-orange-500 focus:ring-orange-500/20 rounded-none resize-none"
                        placeholder="Tell us about your storage needs..."
                        data-testid="input-message"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white font-bold py-4 px-8 transition-all duration-200 shadow-sm hover:shadow-md flex items-center justify-center gap-2"
                      data-testid="submit-button"
                    >
                      {isSubmitting ? (
                        "Sending..."
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Send Message
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
